import React, { useEffect, useState } from 'react';
import FormControl from '../Form/FormControl';
import TextArea from '../Base/TextArea/TextArea';
import SubmitButton from '../Button/SubmitButton';
import TextField from '../Base/TextField/TextField';
import LinkButton from '../Button/LinkButton';
import { Theme } from '../../Enums/Theme.enum';
import Checkbox from '../Base/Checkbox/Checkbox';
import DiagnosisSelect from '../Base/Diagnosis/DiagnosisSelect';
import { FormValues } from '../../Modules/MagicForm/Interfaces/FormValues';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormWrapper from '../Form/FormWrapper';
import SavingSpinner from '../Base/Loading/SavingSpinner';
import useFormDraft from '../Form/FormDraftState/useFormDraft';
import { FormDraftStateKey } from '../Form/FormDraftState/FormDraftStateKey';
import FormMultiplierRemoveButton from '../Form/FormMultiplierRemoveButton';
import RemoveButton from '../Button/RemoveButton';
import FormMultiplier from '../Form/FormMultiplier';
import { useModalContext } from '../../Modules/Modal/Services/ModalContext';
import { remapDiagnosisValues } from '../../Modules/MedicalReport/Services/medicalReport.service';
import useMedicalReport from '../../Modules/MedicalReport/Hooks/useMedicalReport';
import usePatientConsent from '../PatientProfile/Hooks/usePatientConsent';
import PatientNoConsentProvided from '../../Modules/Patient/Components/PatientNoConsentProvided/PatientNoConsentProvided';
import useElectronicSignature from '../../Modules/Certificates/Hooks/useElectronicSignature';
import Loading from '../Loading';
import useScope from '../../Modules/Scope/Hooks/useScope';
import MedicalReportHungaryFields from '../../Modules/MedicalReport/Components/MedicalReportHungaryFields';
import useMedicalReportValidation from '../../Modules/MedicalReport/Hooks/useMedicalReportValidation';

interface MedicalReportDetailDefaultFormProps {
	onSubmit: (values: FormValues) => void;
	defaultData?: any;
	isMeddiCase?: boolean;
	patientId: string;
}

const MedicalReportDetailDefaultForm: React.FC<
	MedicalReportDetailDefaultFormProps
> = ({ onSubmit, defaultData, isMeddiCase, patientId }) => {
	const { isConsentProvided } = usePatientConsent(patientId);
	const { getTextFromDiscourse, canLoadFromDiscourse } =
		useMedicalReport(patientId);
	const { setModalProperties } = useModalContext();
	const [isLoading, setIsLoading] = useState(false);
	const {
		isQualifiedCertificateForElectronicSignatureUsed,
		isLoading: electronicSignatureLoading,
	} = useElectronicSignature();
	const { isHungary, isUnicare } = useScope();
	const { validationSchema } = useMedicalReportValidation();

	const formMethods = useForm({
		defaultValues: defaultData,
		resolver: yupResolver(validationSchema),
	});

	const { resetFormDraft, formDraftIsSaving } = useFormDraft({
		formMethods,
		key: `${FormDraftStateKey.MEDICAL_REPORT}_${patientId}`,
		defaultValues: defaultData,
	});

	// @ts-ignore
	const handleSubmit = async values => {
		setIsLoading(true);
		resetFormDraft();
		values.diagnoses = remapDiagnosisValues(values?.diagnoses);
		onSubmit(values);
		setIsLoading(false);
	};

	const handleOnStartOver = () => {
		resetFormDraft();
		formMethods.reset(defaultData || {});
	};

	useEffect(() => {
		setModalProperties({
			onModalClose: () => resetFormDraft(),
		});
	}, []);

	if (electronicSignatureLoading) {
		return <Loading />;
	}

	return (
		<FormWrapper formMethods={formMethods}>
			<FormControl
				label={'component.medicalReportCzechDefault.nameOfDocument'}
				name='title'
				isRequired={true}>
				<TextField />
			</FormControl>
			{isHungary && <MedicalReportHungaryFields />}
			<FormMultiplier
				name='diagnoses'
				addButtonTitle='base.addAnother'
				createDefault={0}>
				{({ index, itemsCount }) => (
					<>
						<FormControl
							label={'component.medicalReportCzechDefault.diagnosis'}
							name='diagnose'
							isRequired={true}>
							<DiagnosisSelect />
						</FormControl>
						{itemsCount > 1 && index !== 0 && (
							<FormMultiplierRemoveButton>
								<RemoveButton
									onClick={() => void 0}
									title='base.shared.remove'
								/>
							</FormMultiplierRemoveButton>
						)}
					</>
				)}
			</FormMultiplier>
			<FormControl
				label={'component.medicalReportCzechDefault.text'}
				name='text'
				isRequired={true}>
				<TextArea />
			</FormControl>
			{isConsentProvided ? (
				<>
					<div className={'mt-4'}>
						<FormControl name='addToPatientECard' hideRequired={true}>
							<Checkbox tx={'base.addToPatientECard'} />
						</FormControl>
					</div>
					{isUnicare && (
						<div className={'mt-2'}>
							<FormControl name='sendToPatient' hideRequired>
								<Checkbox tx={'component.medicalReport.sendToPatient'} />
							</FormControl>
						</div>
					)}
				</>
			) : (
				<PatientNoConsentProvided patientId={patientId} />
			)}
			{canLoadFromDiscourse && (
				<LinkButton
					onClick={() => {
						getTextFromDiscourse(formMethods);
						// hack to fix bug with form multiplier not updating
						setTimeout(() => {
							getTextFromDiscourse(formMethods);
						}, 0);
					}}
					title={'base.retrieveTextFromDecurse'}
					theme={Theme.BLUE}
				/>
			)}
			{false && (
				<div className={'mt-4'}>
					<FormControl name='electronicSignature' hideRequired={true}>
						<Checkbox
							tx={'component.medicalReportCzechDefault.electronicSignature'}
						/>
					</FormControl>
				</div>
			)}
			{isMeddiCase && (
				<div className={'mt-4'}>
					<FormControl name='contactSpecialist' hideRequired={true}>
						<Checkbox tx={'base.contactSpecialistText'} />
					</FormControl>
				</div>
			)}
			<div className={'d-flex mt-4 justify-content-between'}>
				<SubmitButton
					title={'base.shared.save'}
					onSubmit={handleSubmit}
					disabled={isLoading}
				/>
				{formDraftIsSaving && <SavingSpinner />}
			</div>
		</FormWrapper>
	);
};

export default MedicalReportDetailDefaultForm;
